import { DisplayValue, filterNullish } from '@adaptiva/core/common';
import { LoggingService } from '@adaptiva/core/logging';
import { OverlayService } from '@adaptiva/core/overlays';
import { SnackbarService } from '@adaptiva/core/snackbars';
import { UiService } from '@adaptiva/core/ui';
import { BaseEditorComponent, GlobalEditorService, IEditorInput, IEditorState } from '@adaptiva/data-service';
import { EFileType, ESourceType, ICdnFile, IMetadataObject, MasCdnUploadService, MetadataObject } from '@adaptiva/metadata/common';
import { FileUploaderComponent } from '@adaptiva/widgets/file-uploader';
import { OverlayComponent } from '@adaptiva/widgets/overlay';
import { ChangeDetectorRef, Directive, Input, OnInit, ViewChild } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { map, switchMap, take, takeUntil } from 'rxjs/operators';
import { BaseAuthoringPropertiesComponent } from '../base-authoring-component';

@Directive()
export abstract class BaseContentAuthoringProperties<T> extends BaseAuthoringPropertiesComponent<T> implements OnInit {

	public fileName: string = '';

	public pendingFileUploadMessage: string = '';

	public abstract fileType: EFileType;

	protected file: File;

	@ViewChild('UploadOverlay')
	public abstract uploadOverlay: OverlayComponent;

	@Input()
	public cdnUploadOptions: BehaviorSubject<ICdnFile>;

	public uploadAllowed$: BehaviorSubject<boolean> = new BehaviorSubject(false);

	public sourceTypeOptions: DisplayValue<number>[] = [
		new DisplayValue('None', undefined),
		new DisplayValue('Adaptiva CDN', ESourceType.ADAPTIVA_CDN),
		new DisplayValue('Vendor CDN', ESourceType.VENDOR_CDN),
		new DisplayValue('Vendor CDN with Adaptiva CDN Backup', ESourceType.VENDOR_CDN_WITH_BACKUP),
		new DisplayValue('Customer Provided Files', ESourceType.CUSTOMER_PROVIDES_FILES),
		new DisplayValue('Content not Required', ESourceType.CONTENT_NOT_REQUIRED),
		new DisplayValue('Content No Longer Available', ESourceType.CONTENT_NOT_AVAILABLE)
	];

	constructor(
		public UI: UiService,
		protected _masCdn: MasCdnUploadService,
		protected _globalEditorService: GlobalEditorService,
		protected _snackbarService: SnackbarService,
		protected _overlayService: OverlayService,
		protected _logger: LoggingService,
		protected _cd: ChangeDetectorRef
	) {
		super(UI);
	}

	public ngOnInit(): void {
		this.checkCanEdit();
		this.serverObject$.pipe(
			takeUntil(this.destroyed$)
		).subscribe(() => {
			this.checkCanEdit();
		});
	}

	public checkCanEdit(): void {
		this._globalEditorService.editorInputChange.pipe(
			take(1),
			map((_editorInput: IEditorInput<MetadataObject>) => this._globalEditorService.getEditorState<MetadataObject>(_editorInput.editorId)),
			filterNullish(),
			switchMap((_state: IEditorState<MetadataObject>) => {
				return this._masCdn.isUploadAllowed(_state.value as IMetadataObject);
			}),
		).subscribe((_isAllowed: boolean) => {
			this.uploadAllowed$.next(_isAllowed);
		});
	}

	public submitUploadToCdn(_cdnFileType: string): void {
		if (!this.file || !this.fileName)
			return null;

		const _uploadObject: ICdnFile = {
			property: _cdnFileType,
			fileName: this.fileName,
			fileType: this.fileType,
			file: this.file,
		};

		this.pendingFileUploadMessage = 'Click save to upload ' + this.fileName;
		this.cdnUploadOptions.next(_uploadObject);
		this._enableSaveForUpload();
	}

	public setFileName(_event: string): void {
		this.fileName = _event;
	}

	public openUploadOverlay(): void {
		this.uploadOverlay.open();
	}

	public uploadFileChange(_event: FileUploaderComponent.IContent): void {
		if (!_event.blobContent) {
			this.fileName = null;
			this.file = null;

			return null;
		}

		const _fileContent: File = _event.blobContent[0] as File;

		this.fileName = _fileContent.name;
		this.file = _fileContent;
	}

	private _enableSaveForUpload(): void {
		this._globalEditorService.editorInputChange.pipe(
			take(1),
			map((_input: IEditorInput) => {
				const _editor: BaseEditorComponent<MetadataObject> = this._globalEditorService.getEditorComponent<MetadataObject>(_input.editorId);

				if (_editor?.onChanged)
					_editor.onChanged(true);

				return _editor.state;
			}),
			take(1)
		).subscribe({
			next: (_state: IEditorState<MetadataObject>) => {
				_state.isChanged.next(true);
			},
			error: (e) => {
				const _message: string = this._logger.extractErrorMessage(e);

				this._logger.error(
					'BaseContentAuthoringProperties.submitUploadToCdn',
					_message
				)();
			}
		});
	}

}

